import React, { useState, useEffect, useCallback, useContext } from 'react'
import PropTypes from 'prop-types'
import usePortal from 'react-cool-portal'
import classnames from 'classnames'

// Contexts
import {
  AppContext,
  OverlayContext,
  RouterContext,
  ZillowContext,
} from '../../contexts'

// Constants
import { BOTHEAD_MOUNT_ON_LISTING } from '../../constants/googleAnalytics'
import { positioningTypes, TABS } from '../../constants/display'

// Hooks
import { useTrackBotheadClickAsync } from '../../hooks/clientEvents'
import { useQueryPrefetches } from '../../hooks/queries'
import { useLogGAEvent } from '../../hooks/googleAnalytics'
import { useListingDisplay } from '../../hooks/listing'

// Components
import { If, TargetOverlay } from '../../components'
import AnalyzeModal from './AnalyzeModal'
import { LaunchPadBotHead } from './LaunchTargets'

// Styles
import styles from './ListingLaunchPad.module.scss'

const RenderTarget = ({ positioning, id, children, setModalOpen }) => {
  const { Portal } = usePortal({
    containerId: `launch-pad-${positioning}`,
    onShow: _e => {
      setModalOpen(true)
    },
    onHide: _e => {
      setModalOpen(false)
    },
  })

  switch (positioning) {
    case positioningTypes.RELATIVE:
      return <TargetOverlay targetQuery={`#${id}`}>{children}</TargetOverlay>
    default:
      return (
        <Portal>
          <div
            tabIndex={-1}
            className={classnames(styles.launchPad, {
              [styles.launchPadRight]:
                positioning === positioningTypes.FIXED_RIGHT,
            })}
          >
            {children}
          </div>
        </Portal>
      )
  }
}

RenderTarget.propTypes = {
  positioning: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  setModalOpen: PropTypes.func.isRequired,
}

const ListingLaunchPad = ({
  launcher: Launcher = LaunchPadBotHead,
  positioning = positioningTypes.FIXED_RIGHT,
}) => {
  const id = 'HB_Listing_BotHead'
  const [modalOpen, setModalOpen] = useState(false)
  const { setOverlayIdHistory } = useContext(OverlayContext)
  const { setIframeSrc } = useContext(RouterContext)
  const {
    setMarketTempSliderPrice,
    setAppreciationSliderPercent,
    setActiveTab,
  } = useContext(AppContext)

  const { setUserManuallyEntered } = useContext(ZillowContext)

  const { execute: trackEvent } = useTrackBotheadClickAsync()
  const { address, listingPrice } = useListingDisplay()

  const logGA = useLogGAEvent(BOTHEAD_MOUNT_ON_LISTING)

  const handleCloseAndCleanup = useCallback(() => {
    setModalOpen(false)
    setOverlayIdHistory([])
    setIframeSrc('')
    setActiveTab(TABS.OVERVIEW)
    setUserManuallyEntered && setUserManuallyEntered(false)
  }, [setOverlayIdHistory, setIframeSrc, setActiveTab, setUserManuallyEntered])

  const handleOpen = useCallback(() => {
    setModalOpen(true)
    trackEvent()
  }, [trackEvent])

  useEffect(() => {
    logGA()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setMarketTempSliderPrice(null)
    setAppreciationSliderPercent(null)
  }, [
    address,
    listingPrice,
    setMarketTempSliderPrice,
    setAppreciationSliderPercent,
  ])

  useQueryPrefetches()

  const withOverlay = positioning === positioningTypes.RELATIVE

  return (
    <>
      <Launcher id={id} handleClick={handleOpen} />
      <If condition={modalOpen}>
        <RenderTarget
          positioning={positioning}
          id={id}
          setModalOpen={setModalOpen}
        >
          <AnalyzeModal
            withOverlay={withOverlay}
            onClose={handleCloseAndCleanup}
          />
        </RenderTarget>
      </If>
    </>
  )
}

ListingLaunchPad.propTypes = {
  launcher: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.element,
    PropTypes.func,
  ]),
  positioning: PropTypes.string,
}

export default ListingLaunchPad
